import React from 'react';
import { motion } from 'framer-motion';
import { Zap } from 'lucide-react';

const ProofSection = () => {
  const thirdImage = "https://customer-assets.emergentagent.com/job_codemustakim/artifacts/5cj7hv8m_4FD541DD-7F58-4E6F-BCB9-4A84C10C09F6.jpeg";

  return (
    <section id="proof" className="py-20 lg:py-32 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      
      {/* Background accent */}
      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      {/* Light theme decorative elements */}
      <div className="light-decor-blob-2" style={{ top: '10%', left: '-80px', width: '300px', height: '300px' }} />
      <div className="light-decor-blob-1" style={{ bottom: '5%', right: '-100px', width: '280px', height: '280px' }} />
      <div className="light-decor-circle-1" style={{ bottom: '30%', left: '5%', width: '75px', height: '75px' }} />
      <div className="light-decor-dot-1" style={{ top: '20%', right: '20%' }} />
      <div className="light-decor-line-1" style={{ bottom: '15%', left: '12%', width: '50px' }} />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="relative max-w-md mx-auto lg:max-w-none">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-3xl blur-2xl scale-105 opacity-50" />
              <div className="relative glass-strong rounded-3xl p-2 shadow-xl">
                <div className="relative rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent z-10" />
                  <img
                    src={thirdImage}
                    alt="MD Mustakim Ahmed at a professional event"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto object-cover aspect-[4/5]"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">Proof-First Approach</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 mb-6">
              Actions Over <span className="text-gradient">Words</span>
            </h2>

            {/* Description */}
            <div className="space-y-4 mb-8">
              <p className="text-muted-foreground leading-relaxed text-base font-semibold">
                Share your project idea or requirement first. I'll review it and build a working prototype or demo to show how the solution would look and function.
              </p>
              <p className="text-muted-foreground leading-relaxed text-base font-semibold">
                If you like the approach and quality, you can move forward with me for the full project. If not, there's no obligation and no payment required.
              </p>
              <p className="text-foreground font-bold italic text-base">
                This way, you can evaluate my work through real execution—not promises.
              </p>
            </div>

            {/* Highlight box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="relative p-6 rounded-2xl overflow-hidden mb-8"
            >
              {/* Gradient border */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary via-secondary to-accent p-[1px]">
                <div className="w-full h-full bg-card rounded-2xl" />
              </div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center">
                    <Zap className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">My Commitment</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  I'm happy to take on any technical challenge or build a custom demo 
                  aligned with your requirements to demonstrate my capabilities.
                </p>
              </div>
            </motion.div>

            {/* Key points */}
            <ul className="space-y-4">
              {[
                'Working systems speak louder than portfolios',
                'Real demos over theoretical discussions',
                'Technical challenges welcomed and embraced',
              ].map((point, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.4 }}
                  className="flex items-center text-muted-foreground"
                >
                  <span className="w-2 h-2 rounded-full bg-gradient-primary mr-4 flex-shrink-0" />
                  {point}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export { ProofSection };
